import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { t } from '../i18n.js';
import { formatDate } from '../format.js';
import { loadTrash, onRestoreFromTrash } from '../actions.js';
import {
  trash,
  trashWindow,
  type TrashItem,
} from '../state.js';
import { Action } from './parts.js';
import { IconRestore, IconTrash } from './icons.js';

/**
 * **La corbeille** — *qu'est-ce que je viens de perdre ?*
 *
 * ### Ce que cet écran change, et qui n'est pas un confort
 *
 * Les confirmations livrées la veille protègent du **geste accidentel** ; elles
 * ne protègent pas du **regret**, qui vient plus tard et qu'aucune boîte de
 * dialogue n'attrape. Jusqu'ici, supprimer était définitif : le tombstone était
 * posé sans échéance, aucune route ne listait ce qui avait disparu, et aucune ne
 * le rendait. C'est le seul écran du site dont l'absence rendait un geste
 * irréversible.
 *
 * ### Trois choses qu'il doit dire, et une qu'il ne doit pas
 *
 * - **Quand ça part pour de bon.** La date de purge est **servie** par le
 *   serveur (`purgeAt`), jamais recomposée ici : recalculer `deletedAt + 30 j`
 *   dans le fuseau du navigateur afficherait « expire demain » un jour trop tôt.
 * - **Que les octets sont encore là.** Restaurer ne retélécharge rien et ne
 *   coûte rien — c'est exactement l'objet de la fenêtre de trente jours.
 * - **Qu'un partage restauré retrouve son audience** (D31). Le taire ferait de
 *   la restauration d'un partage une surprise, et c'est le seul geste de cet
 *   écran qui rend quelque chose de visible à d'autres personnes.
 *
 * Ce qu'il ne fait **pas** : offrir une suppression définitive immédiate. Aucune
 * route ne la sert, et ce n'est pas un manque — la corbeille protège du regret,
 * un bouton « vider » y ramènerait le geste dont elle protège. *(Décision de
 * l'utilisateur, 2026-08-17.)*
 */
export function TrashView(): JSX.Element {
  const items = trash.value;

  // Chargée à l'ouverture, et une seule fois : un objet en corbeille n'est dans
  // le miroir local d'aucun appareil — il a quitté les index de listing.
  useEffect(() => {
    if (items === undefined) void loadTrash();
  }, [items === undefined]);

  if (items === undefined) return <div class="stack" data-testid="trash" />;

  if (items.length === 0) {
    return (
      <div class="stack" data-testid="trash">
        <p class="empty" data-testid="trash-empty">
          <IconTrash size={20} />
          <span>{t('trash.empty')}</span>
        </p>
      </div>
    );
  }

  return (
    <div class="stack" data-testid="trash">
      <p class="card-meta" data-testid="trash-window">
        {/* La fenêtre est **servie** : ce texte annonçait « 30 jours » en dur, et
            c'était la troisième copie du même chiffre. Sans réponse de `/me`, la
            phrase se dit sans nombre plutôt qu'avec un nombre inventé. */}
        {trashWindow.value === undefined
          ? t('trash.windowSoft')
          : t('trash.window', { days: trashWindow.value })}
      </p>
      <ul class="cards" data-testid="trash-list">
        {items.map((item) => (
          <TrashRow key={item.Fid ?? item.Aid} item={item} />
        ))}
      </ul>
    </div>
  );
}

function TrashRow({ item }: { readonly item: TrashItem }): JSX.Element {
  const isShare = Boolean(item.Aid);

  return (
    <li class="card" data-testid="trash-item">
      <div class="grow">
        <span class="card-title">{item.Name}</span>
        <span class="card-meta" data-testid="trash-purge">
          {/* **Le fait le plus utile de la ligne**, donc le plus près du nom :
              la date au-delà de laquelle il n'y aura plus rien à restaurer. */}
          {item.purgeAt ? t('trash.purgeAt', { date: formatDate(item.purgeAt) }) : ''}
          {isShare ? ` · ${t('trash.isShare')}` : ''}
        </span>
      </div>
      <Action
        testId="restore"
        variant="primary"
        label={t('trash.restore')}
        onClick={() => void onRestoreFromTrash(item)}
      >
        <IconRestore size={18} />
        <span>{t('trash.restore')}</span>
      </Action>
    </li>
  );
}
